import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  AdminPendingLeadRow,
  DrainPendingLeadsSummary,
  PendingLeadStatus,
} from "./types";

export type UseAdminPendingLeadsOptions = {
  status?: PendingLeadStatus | null;
};

// GET /api/admin/pending-leads?status=
//
// Status tuples onto the bare `pendingLeads.list()` prefix so distinct filter
// states cache independently and the drain mutation's `all`-key invalidate
// sweeps them all.
export function useAdminPendingLeads(
  options: UseAdminPendingLeadsOptions = {},
) {
  const status = options.status ?? null;
  return useQuery<AdminPendingLeadRow[], ApiClientError>({
    queryKey: [...queryKeys.pendingLeads.list(), status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      const qs = params.toString();
      const body = await apiFetch<{ pendingLeads: AdminPendingLeadRow[] }>(
        `/admin/pending-leads${qs ? `?${qs}` : ""}`,
      );
      return body.pendingLeads;
    },
  });
}

// POST /api/admin/pending-leads — force a drain of queued leads. The server
// no-ops (skipped_unconfigured) until HUBSPOT_LEAD_PIPELINE_ID is configured.
// Pessimistic: pushing to HubSpot matters more than speed.
export function useDrainPendingLeads() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: true; summary: DrainPendingLeadsSummary },
    ApiClientError,
    void
  >({
    mutationFn: () =>
      apiFetch<{ ok: true; summary: DrainPendingLeadsSummary }>(
        "/admin/pending-leads",
        { method: "POST" },
      ),
    onError: (err) => {
      toast.error(err.message ?? "Failed to retry queued leads");
    },
    onSuccess: ({ summary }) => {
      if (summary.skipped_unconfigured) {
        toast.message("Lead pipeline not configured", {
          description:
            "Leads stay queued until HUBSPOT_LEAD_PIPELINE_ID is set.",
        });
      } else {
        toast.success(
          `Retried ${summary.scanned} — ${summary.pushed} pushed, ${summary.failed} failed`,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pendingLeads.all });
    },
  });
}
